import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CraftedBy from "../../components/CraftedBy";
import AssistantFAB from "../../components/AssistantFAB";
import { api } from "../../lib/api";
import {
  LayoutDashboard, BookOpen, ScrollText, ListTree, ArrowDownRight, ArrowUpRight,
  Landmark, GitCompareArrows, Percent, Target, FileBarChart2, Settings as SettingsIcon,
  Menu as MenuIcon, ChevronDown, ArrowLeft, LogOut, Crown, Bell, HelpCircle, MapPin, Calendar,
  ChevronLeft, CalendarDays, BarChart3, FileSpreadsheet,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

export const C = {
  canvas: "#FAF6EC", panel: "#FFFFFF", border: "#EFE4D0", borderSoft: "#F5EDDC",
  text: "#3B2722", muted: "#8B7355", softMuted: "#A58F7C",
  burgundy: "#8B1E1E", burgundyDark: "#6E1414", burgundySoft: "#FBEBEB",
  gold: "#C49A4A", green: "#2E7D32", greenTint: "#E4F1E4",
  amber: "#D97706", amberTint: "#FEF3D8", red: "#C62828", redTint: "#FBE4E4",
};

const NAV = [
  { path: "",                     label: "Dashboard",         Icon: LayoutDashboard },
  { path: "daily-branch",         label: "Daily Branch",      Icon: CalendarDays },
  { path: "branch-comparison",    label: "Branch Comparison", Icon: BarChart3 },
  { path: "general-ledger",       label: "General Ledger",    Icon: BookOpen },
  { path: "journals",             label: "Journals",          Icon: ScrollText },
  { path: "chart-of-accounts",    label: "Chart of Accounts", Icon: ListTree },
  { path: "accounts-payable",     label: "Accounts Payable",  Icon: ArrowDownRight },
  { path: "accounts-receivable",  label: "Accounts Receivable", Icon: ArrowUpRight },
  { path: "bank-cash",            label: "Bank & Cash",       Icon: Landmark },
  { path: "reconciliation",       label: "TILL Reconciliation", Icon: GitCompareArrows },
  { path: "settlements",          label: "Bank Settlements",  Icon: FileSpreadsheet },
  { path: "tax-vat",              label: "Tax & VAT",         Icon: Percent },
  { path: "budget-actual",        label: "Budget vs Actual",  Icon: Target },
  { path: "reports-hub",          label: "Reports Hub",       Icon: FileSpreadsheet },
  { path: "statements",           label: "Financial Statements", Icon: FileBarChart2 },
  { path: "reports",              label: "Reports",           Icon: FileBarChart2 },
  { path: "settings",             label: "Settings",          Icon: SettingsIcon },
];

const PAGE_META = {
  "":                    { title: "Financial overview",         subtitle: "Here's your financial overview across all locations." },
  "daily-branch":        { title: "Daily Branch Accounting",    subtitle: "Every financial figure a branch produced on a chosen day — drillable to source." },
  "branch-comparison":   { title: "Branch Comparison",           subtitle: "Revenue · COGS · gross profit · tenders across every branch." },
  "general-ledger":      { title: "General Ledger",              subtitle: "Every posted journal, drillable to its source." },
  "journals":            { title: "Journals",                    subtitle: "Post, view and reverse manual journals." },
  "chart-of-accounts":   { title: "Chart of Accounts",           subtitle: "Configurable accounts + hierarchy." },
  "accounts-payable":    { title: "Accounts Payable",            subtitle: "Aged buckets across all suppliers." },
  "accounts-receivable": { title: "Accounts Receivable",         subtitle: "Outstanding customer + staff balances." },
  "bank-cash":           { title: "Bank & Cash",                 subtitle: "Live balances across tender accounts." },
  "reconciliation":      { title: "TILL Reconciliation",         subtitle: "Cash · card · transfer matching per till/shift." },
  "settlements":         { title: "Bank Settlement Reconciliation", subtitle: "Upload bank/settlement statements and auto-match to recorded payments." },
  "tax-vat":             { title: "Tax & VAT",                   subtitle: "Configurable tax rules + return summary." },
  "budget-actual":       { title: "Budget vs Actual",            subtitle: "Track spend and revenue against plan." },
  "reports":             { title: "Reports",                     subtitle: "GL · TB · P&L · EBITDA · AP · VAT · exports." },
  "reports-hub":         { title: "Accountant Reports Hub",      subtitle: "Revenue · Expenses · Petty Cash · B&D · Inventory — view & export." },
  "statements":          { title: "Financial Statements",        subtitle: "Trading P&L · Funds Release · Daily Revenue · Inventory Valuation · Expenses." },
  "settings":            { title: "Accounting Settings",         subtitle: "Periods · posting rules · exceptions." },
};

export default function AccountingLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selLoc, setSelLoc] = useState(user?.location_id || "all");
  const [now, setNow] = useState(new Date());

  useEffect(() => { api.get("/locations").then(setLocations).catch(() => {}); }, []);
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(iv); }, []);

  const path = location.pathname.replace(/^\/accounting\/?/, "");
  const meta = PAGE_META[path] || PAGE_META[""];
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const first = (user?.name || "there").split(" ")[0];
  const scoped = !!user?.location_id && !user?.is_admin;
  const locName = scoped
    ? locations.find((l) => l.id === user.location_id)?.name || "My location"
    : selLoc === "all" ? "All Locations" : (locations.find((l) => l.id === selLoc)?.name || "Location");
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const yStr = (d) => d.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  const dateStr = `${yesterday.getDate()} – ${yStr(now)}`;

  const currentPeriodKey = now.toISOString().slice(0, 7);
  const periodLabel = now.toLocaleDateString("en-NG", { month: "long", year: "numeric" });
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const remaining = daysInMonth - now.getDate();

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: C.canvas }}>
      {/* SIDEBAR */}
      <aside data-testid="accounting-sidebar"
        className={`${collapsed ? "w-[76px]" : "w-64"} ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-40 h-full flex flex-col transition-all duration-300`}
        style={{ background: C.panel, borderRight: `1px solid ${C.border}` }}>
        <div className="h-[76px] flex items-center gap-3 px-4 border-b" style={{ borderColor: C.border }}>
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: C.gold }}>
            <Crown size={22} color="#3B0D0D" strokeWidth={2.2} />
          </div>
          {!collapsed && (
            <div className="leading-tight min-w-0">
              <div className="font-brand font-bold text-2xl tracking-tight truncate" style={{ color: C.burgundy }}>Sir Monty</div>
              <div className="text-[10px] font-bold tracking-[0.18em] truncate" style={{ color: C.muted }}>ERP ACCOUNTING</div>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {NAV.map((item) => (
            <NavLink key={item.path} to={item.path === "" ? "/accounting" : `/accounting/${item.path}`}
              end={item.path === ""} onClick={() => setMobileOpen(false)}
              data-testid={`acc-nav-${item.path || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-semibold transition-colors ${isActive ? "" : "hover:bg-[#FAF6EC]"}`}
              style={({ isActive }) => ({ background: isActive ? C.burgundySoft : "transparent", color: isActive ? C.burgundy : C.text })}>
              <item.Icon size={18} className="shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {!collapsed && (
          <div className="mx-3 mb-3 p-3 rounded-lg border" style={{ borderColor: C.border, background: "#FDFBF5" }}>
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={13} style={{ color: C.gold }} />
              <span className="text-[10px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>ACCOUNTING PERIOD</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-brand font-semibold text-sm" style={{ color: C.text }}>{periodLabel}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: C.greenTint, color: C.green }}>Open</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full" style={{ background: "#F1E7D3" }}>
              <div className="h-full rounded-full" style={{ width: `${(now.getDate() / daysInMonth) * 100}%`, background: C.gold }} />
            </div>
            <div className="mt-1 text-[10px]" style={{ color: C.softMuted }}>{remaining} day{remaining !== 1 ? "s" : ""} remaining</div>
          </div>
        )}

        <button onClick={() => setCollapsed((c) => !c)} data-testid="acc-collapse-btn"
          className="hidden lg:flex items-center gap-2 px-4 h-12 text-sm font-medium border-t"
          style={{ borderColor: C.border, color: C.muted }}>
          <ChevronLeft className={`transition-transform ${collapsed ? "rotate-180" : ""}`} size={18} />
          {!collapsed && <span>Collapse</span>}
        </button>

        {!collapsed && (
          <div className="px-4 py-3 border-t flex items-center gap-3" style={{ borderColor: C.border }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#F4B400 0%,#E53935 100%)" }}>
              <Crown size={15} color="#FFFFFF" strokeWidth={2.2} />
            </div>
            <div className="font-brand italic leading-tight" style={{ color: C.burgundy }}>
              <div className="text-[11px] font-semibold">Great Food,</div>
              <div className="text-[11px] font-semibold">Great Moments.</div>
            </div>
          </div>
        )}
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* MAIN */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="shrink-0 h-[76px] flex items-center gap-4 px-4 lg:px-6 border-b bg-white" style={{ borderColor: C.border }}>
          <button className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center hover:bg-[#FAF6EC]" onClick={() => setMobileOpen(true)} style={{ color: C.text }}>
            <MenuIcon size={20} />
          </button>
          <button className="hidden lg:flex w-10 h-10 rounded-lg items-center justify-center hover:bg-[#FAF6EC]" onClick={() => setCollapsed((c) => !c)} style={{ color: C.text }}>
            <MenuIcon size={20} />
          </button>
          <div className="min-w-0 shrink pr-4 hidden md:block">
            <div className="font-brand font-bold text-xl leading-tight truncate" style={{ color: C.text }} data-testid="acc-topbar-title">
              {path === "" ? (
                <>{greeting}, {first} <span className="ml-1">👋</span></>
              ) : meta.title}
            </div>
            <div className="text-[12px] truncate" style={{ color: C.softMuted }}>{meta.subtitle}</div>
          </div>

          <div className="flex-1" />

          {!scoped && (
            <div className="hidden lg:flex items-center gap-2 h-11 px-3 rounded-lg border" style={{ borderColor: C.border, background: "#FDFBF5" }}>
              <MapPin size={15} style={{ color: C.gold }} />
              <div className="leading-tight">
                <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>LOCATION</div>
                <Select value={selLoc} onValueChange={setSelLoc}>
                  <SelectTrigger className="h-5 border-0 shadow-none px-0 py-0 font-semibold text-sm bg-transparent focus:ring-0" data-testid="acc-topbar-location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {scoped && (
            <div className="hidden lg:flex items-center gap-2 h-11 px-3 rounded-lg border" style={{ borderColor: C.border, background: "#FDFBF5" }}>
              <MapPin size={15} style={{ color: C.gold }} />
              <div className="leading-tight">
                <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>LOCATION</div>
                <div className="font-semibold text-sm" style={{ color: C.text }}>{locName}</div>
              </div>
            </div>
          )}

          <div className="hidden xl:flex items-center gap-2 h-11 px-3 rounded-lg border" style={{ borderColor: C.border, background: "#FDFBF5" }}>
            <Calendar size={15} style={{ color: C.gold }} />
            <div className="leading-tight">
              <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>DATE</div>
              <div className="font-semibold text-sm tabular-nums" style={{ color: C.text }}>{dateStr}</div>
            </div>
            <ChevronDown size={14} style={{ color: C.softMuted }} />
          </div>

          <button className="relative w-10 h-10 rounded-full flex items-center justify-center border" style={{ borderColor: C.border, background: "#FDFBF5", color: C.text }}>
            <Bell size={17} />
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 text-white" style={{ background: C.burgundy }}>7</span>
          </button>
          <button className="hidden md:flex w-10 h-10 rounded-full items-center justify-center border" style={{ borderColor: C.border, background: "#FDFBF5", color: C.text }}>
            <HelpCircle size={17} />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5" data-testid="acc-user-menu">
                <div className="w-10 h-10 rounded-full text-white flex items-center justify-center text-sm font-bold" style={{ background: C.burgundy }}>{initials}</div>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-semibold" style={{ color: C.text }}>{user?.name}</div>
                  <div className="text-[11px]" style={{ color: C.softMuted }}>{user?.role?.name || "User"}</div>
                </div>
                <ChevronDown size={14} style={{ color: C.softMuted }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs font-normal" style={{ color: C.muted }}>{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/")}><ArrowLeft size={15} className="mr-2" /> Back to ERP</DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} className="text-[#A52222]"><LogOut size={15} className="mr-2" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto" data-testid="acc-main">
          <div className="p-4 lg:p-6 max-w-[1600px] mx-auto w-full">
            <Outlet context={{ locationId: scoped ? user.location_id : (selLoc === "all" ? null : selLoc), period: currentPeriodKey }} />
          </div>
          <footer className="px-4 lg:px-8 pb-4 pt-1 flex items-center justify-between text-[11px]" style={{ color: "#A58F7C" }}>
            <div>© {new Date().getFullYear()} Sir Monty Enterprise ERP · Accounting</div>
            <CraftedBy />
          </footer>
          <AssistantFAB />
        </main>
      </div>
    </div>
  );
}
